'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function normalizeSkillLoadedPart(data) {
    const d = (data ?? {});
    if (typeof d.skillId !== 'string' || d.skillId.length === 0)
        return null;
    if (typeof d.name !== 'string' || d.name.length === 0)
        return null;
    return {
        skillId: d.skillId,
        name: d.name,
        slug: typeof d.slug === 'string' ? d.slug : '',
        scope: d.scope === 'deployment' || d.scope === 'track' || d.scope === 'user_track'
            ? d.scope
            : 'deployment',
        trackId: typeof d.trackId === 'string' || d.trackId === null ? d.trackId : null,
        ownerUserId: typeof d.ownerUserId === 'string' || d.ownerUserId === null ? d.ownerUserId : null,
    };
}
function normalizeApprovalArgs(toolName, args) {
    if (toolName !== 'suggest_skill')
        return args;
    const d = (args ?? {});
    if (d.kind !== 'skill_suggestion_preview')
        return args;
    return {
        kind: 'skill_suggestion_preview',
        operation: d.operation === 'update' ? 'update' : 'create',
        scope: 'user_track',
        name: typeof d.name === 'string' ? d.name : '',
        description: typeof d.description === 'string' ? d.description : '',
        contentPreview: typeof d.contentPreview === 'string' ? d.contentPreview : '',
        requiredCapabilities: Array.isArray(d.requiredCapabilities)
            ? d.requiredCapabilities.filter((entry) => typeof entry === 'string')
            : [],
        optionalCapabilities: Array.isArray(d.optionalCapabilities)
            ? d.optionalCapabilities.filter((entry) => typeof entry === 'string')
            : [],
        ...(typeof d.targetSkillId === 'string' ? { targetSkillId: d.targetSkillId } : {}),
    };
}
const PLAN_PAYLOAD_HASH_RE = /^[a-f0-9]{64}$/;
function isNonEmptyString(value) {
    return typeof value === 'string' && value.length > 0;
}
function hasOwn(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
}
function normalizePlanReferences(references) {
    if (!Array.isArray(references))
        return undefined;
    const normalized = references.flatMap((ref) => {
        const r = (ref ?? {});
        if (!isNonEmptyString(r.type) || !isNonEmptyString(r.id))
            return [];
        return [
            {
                type: r.type,
                id: r.id,
                ...(isNonEmptyString(r.label) ? { label: r.label } : {}),
                ...(isNonEmptyString(r.url) ? { url: r.url } : {}),
            },
        ];
    });
    return normalized.length > 0 ? normalized : undefined;
}
function normalizePlanStep(step) {
    const d = (step ?? {});
    if (!Number.isInteger(d.index) || d.index < 1)
        return null;
    if (!isNonEmptyString(d.toolName))
        return null;
    if (d.kind !== 'read' && d.kind !== 'write')
        return null;
    if (!isNonEmptyString(d.reason))
        return null;
    if (!hasOwn(d, 'argsPreview'))
        return null;
    const references = normalizePlanReferences(d.references);
    return {
        index: d.index,
        toolName: d.toolName,
        kind: d.kind,
        reason: d.reason,
        argsPreview: d.argsPreview,
        ...(references ? { references } : {}),
    };
}
function normalizePlanOptions(options) {
    const d = (options ?? {});
    if (typeof d.allowWholePlanApproval !== 'boolean')
        return null;
    if (!isNonEmptyString(d.wholePlanApprovalLabel))
        return null;
    if (d.perStepApprovalRequiredByDefault !== true)
        return null;
    return {
        allowWholePlanApproval: d.allowWholePlanApproval,
        wholePlanApprovalLabel: d.wholePlanApprovalLabel,
        perStepApprovalRequiredByDefault: true,
    };
}
function normalizePlanActions(actions) {
    const d = (actions ?? {});
    if (!isNonEmptyString(d.decideUrl))
        return null;
    if (!Array.isArray(d.methods))
        return null;
    if (d.methods.length !== 3)
        return null;
    if (d.methods[0] !== 'approve' || d.methods[1] !== 'reject' || d.methods[2] !== 'edit') {
        return null;
    }
    return { decideUrl: d.decideUrl, methods: ['approve', 'reject', 'edit'] };
}
function normalizePlanCopy(copy) {
    const d = (copy ?? {});
    if (!isNonEmptyString(d.title))
        return null;
    if (!isNonEmptyString(d.summary))
        return null;
    if (!isNonEmptyString(d.expiryNotice))
        return null;
    return {
        title: d.title,
        summary: d.summary,
        ...(isNonEmptyString(d.riskNotice) ? { riskNotice: d.riskNotice } : {}),
        expiryNotice: d.expiryNotice,
    };
}
function normalizePlanPendingPart(data) {
    const root = (data ?? {});
    if (root.type !== 'tf-plan-pending')
        return null;
    const plan = (root.plan ?? {});
    if (!isNonEmptyString(plan.id))
        return null;
    if (!isNonEmptyString(plan.sessionId))
        return null;
    if (!isNonEmptyString(plan.trackId))
        return null;
    if (plan.status !== 'pending')
        return null;
    if (!isNonEmptyString(plan.payloadHash) || !PLAN_PAYLOAD_HASH_RE.test(plan.payloadHash)) {
        return null;
    }
    if (!isNonEmptyString(plan.expiresAt))
        return null;
    if (!isNonEmptyString(plan.createdAt))
        return null;
    if (!Array.isArray(plan.steps) || plan.steps.length === 0)
        return null;
    const steps = [];
    for (const rawStep of plan.steps) {
        const step = normalizePlanStep(rawStep);
        if (!step)
            return null;
        steps.push(step);
    }
    const options = normalizePlanOptions(plan.options);
    const actions = normalizePlanActions(root.actions);
    const copy = normalizePlanCopy(root.copy);
    if (!options || !actions || !copy)
        return null;
    return {
        id: plan.id,
        sessionId: plan.sessionId,
        trackId: plan.trackId,
        status: 'pending',
        state: 'pending',
        payloadHash: plan.payloadHash,
        expiresAt: plan.expiresAt,
        createdAt: plan.createdAt,
        steps,
        options,
        actions,
        copy,
    };
}
// ────────────────────────────────────────────────────────────────────────
// Plan 02-06 (D-22, SCUX-07): schedule-preview SSE part normalization.
//
// Three structural normalizers — pending / resolved / error — mirror
// `normalizePlanPendingPart` above. Each returns `null` on any malformed
// payload (missing field, wrong discriminator, non-hex hash, non-string
// scalar, etc.) so the grouper drops the part silently. NO `console.*`
// is emitted on a `null` return per UI-SPEC.md Graceful Degradation +
// the existing convention used across memory, approval, plan, and
// plan-activation parts.
// ────────────────────────────────────────────────────────────────────────
const SCHEDULE_PREVIEW_PENDING_TYPE = 'tf-schedule-preview-pending';
const SCHEDULE_PREVIEW_RESOLVED_TYPE = 'tf-schedule-preview-resolved';
const SCHEDULE_PREVIEW_ERROR_TYPE = 'tf-schedule-preview-error';
const SCHEDULE_PREVIEW_ALLOWED_KEYS = [
    'type',
    'scheduleDraftId',
    'cadenceLabel',
    'cronExpr',
    'timezone',
    'canonicalPrompt',
    'toolAllowlist',
    'availableTools',
    'wholePlanPreapproved',
    'wholePlanPreapprovedWarningText',
    'wholePlanPreapprovalUiEnabled',
    'nextRunAt',
    'payloadHash',
    'expiresAt',
];
function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function isStringArray(value) {
    return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}
export function normalizeSchedulePreviewPendingPart(data) {
    if (!isPlainObject(data))
        return null;
    if (data.type !== SCHEDULE_PREVIEW_PENDING_TYPE)
        return null;
    if (!isNonEmptyString(data.scheduleDraftId))
        return null;
    if (!isNonEmptyString(data.cadenceLabel))
        return null;
    if (!isNonEmptyString(data.cronExpr))
        return null;
    if (!isNonEmptyString(data.timezone))
        return null;
    if (!isNonEmptyString(data.canonicalPrompt))
        return null;
    if (!isStringArray(data.toolAllowlist))
        return null;
    if (!isStringArray(data.availableTools))
        return null;
    if (typeof data.wholePlanPreapproved !== 'boolean')
        return null;
    if (!isNonEmptyString(data.wholePlanPreapprovedWarningText))
        return null;
    // COP-096 rule 5: optional env-gated UI flag. Absent → treated as false.
    if (data.wholePlanPreapprovalUiEnabled !== undefined &&
        typeof data.wholePlanPreapprovalUiEnabled !== 'boolean') {
        return null;
    }
    if (!isNonEmptyString(data.nextRunAt))
        return null;
    if (!isNonEmptyString(data.payloadHash) || !PLAN_PAYLOAD_HASH_RE.test(data.payloadHash)) {
        return null;
    }
    if (!isNonEmptyString(data.expiresAt))
        return null;
    // additionalProperties:false posture — reject smuggled extra keys
    // defensively (the server schema already does this at the boundary, but
    // a malformed proxy could re-inject; per D-32 we never propagate extras).
    for (const key of Object.keys(data)) {
        if (!SCHEDULE_PREVIEW_ALLOWED_KEYS.includes(key)) {
            return null;
        }
    }
    return {
        type: SCHEDULE_PREVIEW_PENDING_TYPE,
        scheduleDraftId: data.scheduleDraftId,
        cadenceLabel: data.cadenceLabel,
        cronExpr: data.cronExpr,
        timezone: data.timezone,
        canonicalPrompt: data.canonicalPrompt,
        toolAllowlist: data.toolAllowlist,
        availableTools: data.availableTools,
        wholePlanPreapproved: data.wholePlanPreapproved,
        wholePlanPreapprovedWarningText: data.wholePlanPreapprovedWarningText,
        ...(typeof data.wholePlanPreapprovalUiEnabled === 'boolean'
            ? { wholePlanPreapprovalUiEnabled: data.wholePlanPreapprovalUiEnabled }
            : {}),
        nextRunAt: data.nextRunAt,
        payloadHash: data.payloadHash,
        expiresAt: data.expiresAt,
    };
}
function normalizeSchedulePreviewResolvedPart(data) {
    if (!isPlainObject(data))
        return null;
    if (data.type !== SCHEDULE_PREVIEW_RESOLVED_TYPE)
        return null;
    if (!isNonEmptyString(data.scheduleDraftId))
        return null;
    const decision = data.decision;
    if (decision !== 'approved' && decision !== 'rejected' && decision !== 'expired') {
        return null;
    }
    const scheduleId = typeof data.scheduleId === 'string' && data.scheduleId.length > 0 ? data.scheduleId : undefined;
    const nextRunAt = typeof data.nextRunAt === 'string' && data.nextRunAt.length > 0 ? data.nextRunAt : undefined;
    return {
        type: SCHEDULE_PREVIEW_RESOLVED_TYPE,
        scheduleDraftId: data.scheduleDraftId,
        decision,
        ...(scheduleId ? { scheduleId } : {}),
        ...(nextRunAt ? { nextRunAt } : {}),
    };
}
function normalizeSchedulePreviewErrorPart(data) {
    if (!isPlainObject(data))
        return null;
    if (data.type !== SCHEDULE_PREVIEW_ERROR_TYPE)
        return null;
    if (typeof data.code !== 'string' || data.code.length === 0)
        return null;
    const scheduleDraftId = typeof data.scheduleDraftId === 'string' && data.scheduleDraftId.length > 0
        ? data.scheduleDraftId
        : undefined;
    return {
        type: SCHEDULE_PREVIEW_ERROR_TYPE,
        code: data.code,
        ...(scheduleDraftId ? { scheduleDraftId } : {}),
    };
}
/**
 * Detect a `memory_confirm` / `memory_dismiss` envelope encoded as the text body
 * of a user chat message (D-12, COP-051). Used by the grouper to suppress the
 * envelope from the visible message list — the user clicked Save/Dismiss on the
 * card, they should not see the raw JSON they "sent" appear as a chat bubble.
 * The persisted `tf_chat_message` audit row stays intact server-side.
 *
 * Conservative match: substring check rather than full JSON.parse, so a user
 * message that happens to contain the substring AFTER other text (extremely
 * unlikely; envelopes are emitted via `sendMessage(JSON.stringify(...))` and
 * are the entire body) is NOT suppressed.
 */
export function isMemoryEnvelopeText(text) {
    const trimmed = text.trim();
    if (!trimmed.startsWith('{') || !trimmed.endsWith('}'))
        return false;
    if (!trimmed.includes('"kind":"memory_confirm"') &&
        !trimmed.includes('"kind":"memory_dismiss"')) {
        return false;
    }
    // Cheap JSON sanity check — if it does not parse, treat as user prose.
    try {
        const parsed = JSON.parse(trimmed);
        return parsed?.kind === 'memory_confirm' || parsed?.kind === 'memory_dismiss';
    }
    catch {
        return false;
    }
}
/**
 * Detect a `doc_share_confirm` / `doc_share_dismiss` envelope encoded as the
 * text body of a user chat message (IMEM-09, HUB-065, COP-051). Mirrors
 * `isMemoryEnvelopeText`: the grouper suppresses the envelope from the visible
 * message list — the user clicked Confirm/Dismiss on the card, the raw JSON
 * they "sent" must not appear as a chat bubble. The persisted `tf_chat_message`
 * audit row stays intact server-side.
 */
export function isDocShareEnvelopeText(text) {
    const trimmed = text.trim();
    if (!trimmed.startsWith('{') || !trimmed.endsWith('}'))
        return false;
    if (!trimmed.includes('"kind":"doc_share_confirm"') &&
        !trimmed.includes('"kind":"doc_share_dismiss"')) {
        return false;
    }
    try {
        const parsed = JSON.parse(trimmed);
        return parsed?.kind === 'doc_share_confirm' || parsed?.kind === 'doc_share_dismiss';
    }
    catch {
        return false;
    }
}
/**
 * Detect a `forget_confirm` / `forget_dismiss` envelope encoded as the text
 * body of a user chat message (IMEM-19, COP-105). Mirrors
 * `isDocShareEnvelopeText`: the grouper suppresses the envelope from the visible
 * message list — the user clicked Forget/Dismiss on the card, the raw JSON they
 * "sent" must not appear as a chat bubble. The persisted `tf_chat_message` audit
 * row stays intact server-side.
 */
export function isForgetMemoryEnvelopeText(text) {
    const trimmed = text.trim();
    if (!trimmed.startsWith('{') || !trimmed.endsWith('}'))
        return false;
    if (!trimmed.includes('"kind":"forget_confirm"') &&
        !trimmed.includes('"kind":"forget_dismiss"')) {
        return false;
    }
    try {
        const parsed = JSON.parse(trimmed);
        return parsed?.kind === 'forget_confirm' || parsed?.kind === 'forget_dismiss';
    }
    catch {
        return false;
    }
}
/**
 * Terminal states for every `data-memory-*` part by `suggestionId`. Delegates
 * to the single `collectAllTerminalStates` walk (which owns the cross-turn
 * merge + last-write-wins contract described there) and returns its `memory`
 * map. Kept as a named export for existing call sites and tests.
 */
export function collectMemorySuggestionTerminalStates(messages) {
    return collectAllTerminalStates(messages).memory;
}
/**
 * Terminal states for the `data-doc-share-*` family (IMEM-09, HUB-065,
 * COP-051) by `suggestionId`. Delegates to the single `collectAllTerminalStates`
 * walk (which owns the cross-turn merge + last-write-wins contract) and returns
 * its `docShare` map.
 */
export function collectDocShareTerminalStates(messages) {
    return collectAllTerminalStates(messages).docShare;
}
/**
 * Terminal states for the `data-forget-memory-*` family (IMEM-19, COP-105) by
 * `suggestionId`. Delegates to the single `collectAllTerminalStates` walk (which
 * owns the cross-turn merge + last-write-wins contract) and returns its
 * `forgetMemory` map.
 */
export function collectForgetMemoryTerminalStates(messages) {
    return collectAllTerminalStates(messages).forgetMemory;
}
/**
 * Pure folding step for the `share_document` confirmation card (IMEM-09,
 * HUB-065, COP-051). Given the current `DocShareSuggestionPart` (or `null`
 * before the opening `data-doc-share-suggestion` is seen) and the next raw
 * stream part, returns the next card state:
 *
 *   - `data-doc-share-suggestion` opens a `pending` card from
 *     `{ suggestionId, documentId, filename?, scope?, grant, revoke }`. A
 *     malformed payload (missing `suggestionId`/`documentId`, an invalid
 *     `scope`, or NO change at all — no valid scope and no grant/revoke targets)
 *     leaves `prev` unchanged.
 *   - `data-doc-share-updated` / `-dismissed` / `-error` fold the terminal
 *     state in place (matching `suggestionId`); `-error` also carries
 *     `errorCode` from the server's `data.code`. A terminal part with no
 *     opened card (`prev === null`) or a non-matching `suggestionId` is
 *     ignored.
 *   - Any other part type leaves `prev` unchanged.
 *
 * Pure + DOM-free so the card's part-folding is unit-testable directly.
 */
function parseTargets(value) {
    // Keep only well-formed { userId, label } pairs.
    return Array.isArray(value)
        ? value.flatMap((t) => {
            const tt = (t ?? {});
            return typeof tt.userId === 'string' && typeof tt.label === 'string'
                ? [{ userId: tt.userId, label: tt.label }]
                : [];
        })
        : [];
}
export function applyDocSharePart(prev, part) {
    if (part.type === 'data-doc-share-suggestion') {
        const d = (part.data ?? {});
        if (!d.suggestionId || !d.documentId)
            return prev;
        const scope = d.scope === 'private' || d.scope === 'track' || d.scope === 'everyone' ? d.scope : undefined;
        const grant = parseTargets(d.grant);
        const revoke = parseTargets(d.revoke);
        // Composite must carry at least one change, else it is not a valid card.
        if (scope === undefined && grant.length === 0 && revoke.length === 0)
            return prev;
        return {
            suggestionId: d.suggestionId,
            documentId: d.documentId,
            ...(typeof d.filename === 'string' ? { filename: d.filename } : {}),
            ...(scope !== undefined ? { scope } : {}),
            grant,
            revoke,
            state: 'pending',
        };
    }
    if (part.type === 'data-doc-share-updated' ||
        part.type === 'data-doc-share-dismissed' ||
        part.type === 'data-doc-share-error') {
        if (!prev)
            return prev;
        const d = (part.data ?? {});
        if (d.suggestionId !== prev.suggestionId)
            return prev;
        if (part.type === 'data-doc-share-updated') {
            return { ...prev, state: 'updated' };
        }
        if (part.type === 'data-doc-share-dismissed') {
            return { ...prev, state: 'dismissed' };
        }
        return {
            ...prev,
            state: 'error',
            ...(typeof d.code === 'string' ? { errorCode: d.code } : {}),
        };
    }
    return prev;
}
/** Keep only well-formed `{ id, content, scope }` memory rows (scope ∈ global|track). */
function parseForgetMemories(value) {
    return Array.isArray(value)
        ? value.flatMap((m) => {
            const mm = (m ?? {});
            const scope = mm.scope === 'global' || mm.scope === 'track' ? mm.scope : undefined;
            return typeof mm.id === 'string' && typeof mm.content === 'string' && scope !== undefined
                ? [{ id: mm.id, content: mm.content, scope }]
                : [];
        })
        : [];
}
/**
 * Pure folding step for the `forget_memory` confirmation card (IMEM-19,
 * COP-105). Mirrors `applyDocSharePart`. Given the current `ForgetMemoryPart`
 * (or `null` before the opening `data-forget-memory` is seen) and the next raw
 * stream part, returns the next card state:
 *
 *   - `data-forget-memory` opens a `pending` card from
 *     `{ suggestionId, memories: [{ id, content, scope }] }`. A malformed
 *     payload (missing `suggestionId` or NO well-formed memory rows) leaves
 *     `prev` unchanged.
 *   - `data-forget-memory-removed` / `-dismissed` / `-error` fold the terminal
 *     state in place (matching `suggestionId`); `-error` also carries the
 *     server's `code` / `message`. A terminal part with no opened card
 *     (`prev === null`) or a non-matching `suggestionId` is ignored.
 *   - Any other part type leaves `prev` unchanged.
 */
export function applyForgetMemoryPart(prev, part) {
    if (part.type === 'data-forget-memory') {
        const d = (part.data ?? {});
        if (!d.suggestionId)
            return prev;
        const memories = parseForgetMemories(d.memories);
        // The card must NAME at least one memory to be actionable (IMEM-19).
        if (memories.length === 0)
            return prev;
        return {
            suggestionId: d.suggestionId,
            memories,
            state: 'pending',
        };
    }
    if (part.type === 'data-forget-memory-removed' ||
        part.type === 'data-forget-memory-dismissed' ||
        part.type === 'data-forget-memory-error') {
        if (!prev)
            return prev;
        const d = (part.data ?? {});
        if (d.suggestionId !== prev.suggestionId)
            return prev;
        if (part.type === 'data-forget-memory-removed') {
            return { ...prev, state: 'removed' };
        }
        if (part.type === 'data-forget-memory-dismissed') {
            return { ...prev, state: 'dismissed' };
        }
        return {
            ...prev,
            state: 'error',
            ...(typeof d.code === 'string' ? { errorCode: d.code } : {}),
            ...(typeof d.message === 'string' ? { errorMessage: d.message } : {}),
        };
    }
    return prev;
}
/**
 * Terminal SSE states for every `data-approval-*` part by `approvalId`
 * (including the `code === 'expired'` → `expired` mapping). Delegates to the
 * single `collectAllTerminalStates` walk (which owns the cross-turn merge +
 * last-write-wins contract) and returns its `approval` map.
 */
export function collectApprovalTerminalStates(messages) {
    return collectAllTerminalStates(messages).approval;
}
/**
 * Terminal states for reloaded plan cards by `planId`, filtered to the 4-value
 * `PlanGroupTerminalState` union. Delegates to the single
 * `collectAllTerminalStates` walk (which owns the decision-filter + cross-turn
 * merge contract) and returns its `plan` map.
 */
export function collectPlanTerminalStates(messages) {
    return collectAllTerminalStates(messages).plan;
}
/**
 * Terminal SSE states for every `data-tf-schedule-preview-*` part by
 * `scheduleDraftId` (routing payloads through the normalize helpers).
 * Delegates to the single `collectAllTerminalStates` walk (which owns the
 * cross-turn merge + last-write-wins contract) and returns its
 * `schedulePreview` map.
 */
export function collectSchedulePreviewTerminalStates(messages) {
    return collectAllTerminalStates(messages).schedulePreview;
}
/**
 * Single-pass replacement for the five per-family `collect*TerminalStates`
 * pre-passes. ONE outer walk over the full message list dispatches every
 * terminal part to its family's `Map` in one pass; `CopilotMessages` calls
 * this once per render (avoiding 5× full-list scans) and the five legacy
 * collectors delegate to it (avoid-dedup: the walk logic lives here only).
 *
 * Family divergences preserved verbatim from the original collectors:
 *   - schedule-preview routes payloads through
 *     `normalizeSchedulePreviewResolvedPart` / `normalizeSchedulePreviewErrorPart`;
 *   - approval maps `code === 'expired'` → `{ state: 'expired' }`;
 *   - plan FILTERS decisions to the 4-value `PlanGroupTerminalState` union;
 *   - every family skips a part whose key (suggestionId / approvalId / planId /
 *     scheduleDraftId) is missing.
 *
 * Last-write-wins per key within each family (iteration is message-order then
 * part-order), which preserves the most recent server state on stream replays.
 */
export function collectAllTerminalStates(messages) {
    const memory = new Map();
    const docShare = new Map();
    const forgetMemory = new Map();
    const approval = new Map();
    const plan = new Map();
    const schedulePreview = new Map();
    for (const msg of messages) {
        const parts = msg?.parts;
        if (!parts)
            continue;
        for (const p of parts) {
            // ── memory-suggestion family ──
            if (p.type === 'data-memory-saved') {
                const d = (p.data ?? {});
                if (d.suggestionId)
                    memory.set(d.suggestionId, { type: 'saved', memoryId: d.memoryId });
            }
            else if (p.type === 'data-memory-dismissed') {
                const d = (p.data ?? {});
                if (d.suggestionId)
                    memory.set(d.suggestionId, { type: 'dismissed' });
            }
            else if (p.type === 'data-memory-suggestion-error') {
                const d = (p.data ?? {});
                if (d.suggestionId) {
                    memory.set(d.suggestionId, {
                        type: 'error',
                        errorCode: d.code,
                        errorMessage: d.message,
                    });
                }
            }
            // ── doc-share family ──
            else if (p.type === 'data-doc-share-updated') {
                const d = (p.data ?? {});
                if (d.suggestionId)
                    docShare.set(d.suggestionId, { type: 'updated' });
            }
            else if (p.type === 'data-doc-share-dismissed') {
                const d = (p.data ?? {});
                if (d.suggestionId)
                    docShare.set(d.suggestionId, { type: 'dismissed' });
            }
            else if (p.type === 'data-doc-share-error') {
                const d = (p.data ?? {});
                if (d.suggestionId) {
                    docShare.set(d.suggestionId, { type: 'error', errorCode: d.code });
                }
            }
            // ── forget-memory family (IMEM-19, COP-105) ──
            else if (p.type === 'data-forget-memory-removed') {
                const d = (p.data ?? {});
                if (d.suggestionId)
                    forgetMemory.set(d.suggestionId, { type: 'removed' });
            }
            else if (p.type === 'data-forget-memory-dismissed') {
                const d = (p.data ?? {});
                if (d.suggestionId)
                    forgetMemory.set(d.suggestionId, { type: 'dismissed' });
            }
            else if (p.type === 'data-forget-memory-error') {
                const d = (p.data ?? {});
                if (d.suggestionId) {
                    forgetMemory.set(d.suggestionId, {
                        type: 'error',
                        errorCode: d.code,
                        errorMessage: d.message,
                    });
                }
            }
            // ── approval family (code === 'expired' → expired) ──
            else if (p.type === 'data-approval-resolved') {
                const d = (p.data ?? {});
                if (!d.approvalId || !d.action)
                    continue;
                if (d.action === 'approve') {
                    approval.set(d.approvalId, {
                        state: 'approved',
                        ...(d.execution ? { execution: d.execution } : {}),
                    });
                }
                else if (d.action === 'reject') {
                    approval.set(d.approvalId, { state: 'rejected', reason: d.reason });
                }
                else if (d.action === 'edit') {
                    approval.set(d.approvalId, { state: 'edited', reason: d.reason });
                }
            }
            else if (p.type === 'data-approval-error') {
                const d = (p.data ?? {});
                if (!d.approvalId)
                    continue;
                // ATRU-01 precedence rule — the live failure seam emits
                // `data-approval-resolved{execution:'failed'}` and THEN the
                // `data-approval-error` that carries the user-facing detail. Plain
                // last-write-wins would flatten the richer card ("Approved" +
                // "Execution failed") into a bare `error` state, so an error part
                // arriving on an approvalId ALREADY at approved+failed is ignored for
                // terminal-state purposes. It neither enriches nor replaces; its
                // message still renders wherever it renders today. Every OTHER case —
                // including `code === 'expired'` — keeps last-write-wins unchanged.
                const prior = approval.get(d.approvalId);
                if (prior?.state === 'approved' && prior.execution === 'failed')
                    continue;
                if (d.code === 'expired') {
                    approval.set(d.approvalId, { state: 'expired' });
                }
                else {
                    approval.set(d.approvalId, {
                        state: 'error',
                        errorCode: d.code,
                        errorMessage: d.message,
                    });
                }
            }
            // ── plan family (decisions FILTERED to the 4-value union) ──
            else if (p.type === 'data-tf-plan-resolved') {
                const d = (p.data ?? {});
                if (!d.planId)
                    continue;
                if (d.decision === 'approved' || d.decision === 'rejected' || d.decision === 'edited' || d.decision === 'expired') {
                    plan.set(d.planId, d.decision);
                }
            }
            // ── schedule-preview family (routed through normalize helpers) ──
            else if (p.type === 'data-tf-schedule-preview-resolved') {
                const resolved = normalizeSchedulePreviewResolvedPart(p.data);
                if (!resolved)
                    continue;
                const summary = {};
                if (resolved.scheduleId)
                    summary.scheduleId = resolved.scheduleId;
                if (resolved.nextRunAt)
                    summary.nextRunAt = resolved.nextRunAt;
                if (resolved.decision === 'approved') {
                    schedulePreview.set(resolved.scheduleDraftId, {
                        terminalState: 'approved',
                        resolvedAt: summary,
                    });
                }
                else if (resolved.decision === 'rejected') {
                    schedulePreview.set(resolved.scheduleDraftId, {
                        terminalState: 'rejected',
                        resolvedAt: summary,
                    });
                }
                else if (resolved.decision === 'expired') {
                    schedulePreview.set(resolved.scheduleDraftId, {
                        terminalState: 'expired',
                        resolvedAt: summary,
                    });
                }
            }
            else if (p.type === 'data-tf-schedule-preview-error') {
                const errorPart = normalizeSchedulePreviewErrorPart(p.data);
                if (!errorPart || !errorPart.scheduleDraftId)
                    continue;
                schedulePreview.set(errorPart.scheduleDraftId, {
                    terminalState: 'error',
                    resolvedAt: { errorCode: errorPart.code },
                });
            }
        }
    }
    return { memory, docShare, forgetMemory, approval, plan, schedulePreview };
}
// ── §2 per-family part handlers ──────────────────────────────────────────────
// One module-private handler per `part.type` family, each carrying its own
// `part.type` predicate and returning `true` once it owns (consumes) the part.
// `groupParts` calls them in the exact legacy branch order, `continue`-ing on
// the first `true`. Reverse-scan merges operate on the passed per-message
// `groups` array, preserving same-message-only merge semantics.
function handleTextPart(part, groups, opts) {
    if (part.type !== 'text')
        return false;
    const text = part.text ?? '';
    // Hide memory_confirm / memory_dismiss envelope echoes from the user's
    // own bubble — the card's terminal state is the user-visible feedback.
    if (opts?.role === 'user' &&
        (isMemoryEnvelopeText(text) ||
            isDocShareEnvelopeText(text) ||
            isForgetMemoryEnvelopeText(text)))
        return true;
    // Suppress empty / whitespace-only text parts so they don't render as
    // empty chat bubbles. The continuation-turn user message produced by
    // `sendMessage({ pendingActionId })` carries an empty text part with
    // no other content (`@ai-sdk/react` always emits at least one part);
    // pre-fix this rendered as a black-rectangle user bubble. Likewise
    // the assistant SSE occasionally closes with a 0-length text block
    // after a resume — pre-fix that rendered as an empty "Assistant"
    // bubble below the real reply. Regression-pinned by
    // `widget-empty-bubble-suppression.test.ts`.
    if (text.trim().length === 0)
        return true;
    groups.push({ type: 'text', text });
    return true;
}
function handleSkillLoadedPart(part, groups) {
    if (part.type !== 'data-skill-loaded')
        return false;
    const skill = normalizeSkillLoadedPart(part.data);
    if (!skill)
        return true;
    groups.push({ type: 'skill-loaded', skill });
    return true;
}
function handlePlanPart(part, groups, opts) {
    if (part.type !== 'data-tf-plan-pending')
        return false;
    const plan = normalizePlanPendingPart(part.data);
    if (!plan)
        return true;
    const initialState = opts?.planTerminalStates?.get(plan.id);
    groups.push({ type: 'plan', plan, ...(initialState ? { initialState } : {}) });
    // COP-092 retire plan-mode: removed the `data-tf-plan-activation-notice`
    // grouping branch with the activation-notice SSE part.
    return true;
}
function handleSchedulePreviewPart(part, groups, opts) {
    if (part.type !== 'data-tf-schedule-preview-pending')
        return false;
    // Plan 02-06 (D-22, SCUX-07): schedule consent gate. Server emits
    // exactly one preview-pending part per draft after `create_schedule`
    // persists the tf_schedule_draft row (Plan 02-05). The grouper
    // normalizes the payload (graceful degradation: malformed →
    // null → silent drop) and merges any cross-message terminal state
    // collected by `collectSchedulePreviewTerminalStates` over the full
    // message list.
    const preview = normalizeSchedulePreviewPendingPart(part.data);
    if (!preview)
        return true;
    const terminal = opts?.schedulePreviewTerminalStates?.get(preview.scheduleDraftId);
    groups.push({
        type: 'schedule-preview',
        preview,
        terminalState: terminal?.terminalState ?? 'pending',
        ...(terminal?.resolvedAt ? { resolvedAt: terminal.resolvedAt } : {}),
    });
    return true;
}
function handleMemoryPart(part, groups, opts) {
    if (part.type === 'data-memory-suggestion') {
        const d = (part.data ?? {});
        if (!d.suggestionId)
            return true;
        // Look up a cross-message terminal state for this suggestionId — if the
        // user clicked Save/Dismiss and the terminal SSE part landed on a later
        // assistant message, the suggestion should render in its terminal state
        // immediately rather than as pending.
        const terminal = opts?.terminalStates?.get(d.suggestionId);
        const suggestion = {
            suggestionId: d.suggestionId,
            content: d.content ?? '',
            scope: d.scope ?? 'global',
            trackId: d.trackId,
            // IMEM-14 — pass the refine-variant fields through only when present so a
            // plain suggestion's grouped shape is byte-identical to before.
            ...(typeof d.updatesMemoryId === 'string' ? { updatesMemoryId: d.updatesMemoryId } : {}),
            ...(typeof d.previousContent === 'string' ? { previousContent: d.previousContent } : {}),
            // DRM-04 / COP-109 — dream-proposal fields (milestone-17 task-06
            // hydration), passed through only when present so a legacy extraction
            // suggestion's grouped shape is byte-identical to before.
            ...(typeof d.origin === 'string' ? { origin: d.origin } : {}),
            ...(typeof d.kind === 'string' ? { kind: d.kind } : {}),
            ...(Array.isArray(d.sourceContents) ? { sourceContents: d.sourceContents } : {}),
            state: terminal?.type ?? 'pending',
            ...(terminal?.type === 'saved' ? { memoryId: terminal.memoryId } : {}),
            ...(terminal?.type === 'error'
                ? { errorCode: terminal.errorCode, errorMessage: terminal.errorMessage }
                : {}),
        };
        groups.push({ type: 'memory-suggestion', suggestion });
        return true;
    }
    if (part.type === 'data-memory-saved' ||
        part.type === 'data-memory-dismissed' ||
        part.type === 'data-memory-suggestion-error') {
        const d = (part.data ?? {});
        if (!d.suggestionId)
            return true;
        // Same-message merge: find the most recent pending memory-suggestion
        // group within THIS message's parts and upgrade it in place. Standalone
        // terminal parts on later messages were already absorbed by `terminalStates`
        // during the cross-message pre-pass and produce nothing here.
        for (let i = groups.length - 1; i >= 0; i--) {
            const g = groups[i];
            if (g.type === 'memory-suggestion' && g.suggestion.suggestionId === d.suggestionId) {
                if (part.type === 'data-memory-saved') {
                    g.suggestion = { ...g.suggestion, state: 'saved', memoryId: d.memoryId };
                }
                else if (part.type === 'data-memory-dismissed') {
                    g.suggestion = { ...g.suggestion, state: 'dismissed' };
                }
                else {
                    g.suggestion = {
                        ...g.suggestion,
                        state: 'error',
                        errorCode: d.code,
                        errorMessage: d.message,
                    };
                }
                break;
            }
        }
        return true;
    }
    return false;
}
function handleDocSharePart(part, groups, opts) {
    if (part.type === 'data-doc-share-suggestion') {
        // IMEM-09 / HUB-065 / COP-051: a `data-doc-share-suggestion` part opens a
        // `{ type: 'doc-share-suggestion', suggestion }` entry. Mirrors the
        // data-memory-suggestion branch — fold via the pure `applyDocSharePart`
        // helper, then upgrade in place when the cross-message terminal-state
        // map has an entry for this suggestionId (the confirm/dismiss envelope's
        // terminal SSE part landed on a later assistant message).
        let suggestion = applyDocSharePart(null, part);
        if (!suggestion)
            return true;
        const terminal = opts?.docShareTerminalStates?.get(suggestion.suggestionId);
        if (terminal) {
            if (terminal.type === 'updated') {
                suggestion = { ...suggestion, state: 'updated' };
            }
            else if (terminal.type === 'dismissed') {
                suggestion = { ...suggestion, state: 'dismissed' };
            }
            else {
                suggestion = {
                    ...suggestion,
                    state: 'error',
                    ...(terminal.errorCode ? { errorCode: terminal.errorCode } : {}),
                };
            }
        }
        groups.push({ type: 'doc-share-suggestion', suggestion });
        return true;
    }
    if (part.type === 'data-doc-share-updated' ||
        part.type === 'data-doc-share-dismissed' ||
        part.type === 'data-doc-share-error') {
        // Same-message merge: find the most recent pending doc-share-suggestion
        // group within THIS message's parts and fold the terminal state in via
        // the pure helper. Standalone terminal parts on later messages were
        // already absorbed by `docShareTerminalStates` and produce nothing here.
        const d = (part.data ?? {});
        if (!d.suggestionId)
            return true;
        for (let i = groups.length - 1; i >= 0; i--) {
            const g = groups[i];
            if (g.type === 'doc-share-suggestion' &&
                g.suggestion.suggestionId === d.suggestionId) {
                const next = applyDocSharePart(g.suggestion, part);
                if (next)
                    g.suggestion = next;
                break;
            }
        }
        return true;
    }
    return false;
}
function handleForgetMemoryPart(part, groups, opts) {
    if (part.type === 'data-forget-memory') {
        // IMEM-19 / COP-105: a `data-forget-memory` part opens a
        // `{ type: 'forget-memory', suggestion }` entry. Mirrors the
        // data-doc-share-suggestion branch — fold via the pure `applyForgetMemoryPart`
        // helper, then upgrade in place when the cross-message terminal-state map
        // has an entry for this suggestionId (the confirm/dismiss envelope's
        // terminal SSE part landed on a later assistant message).
        let suggestion = applyForgetMemoryPart(null, part);
        if (!suggestion)
            return true;
        const terminal = opts?.forgetMemoryTerminalStates?.get(suggestion.suggestionId);
        if (terminal) {
            if (terminal.type === 'removed') {
                suggestion = { ...suggestion, state: 'removed' };
            }
            else if (terminal.type === 'dismissed') {
                suggestion = { ...suggestion, state: 'dismissed' };
            }
            else {
                suggestion = {
                    ...suggestion,
                    state: 'error',
                    ...(terminal.errorCode ? { errorCode: terminal.errorCode } : {}),
                    ...(terminal.errorMessage ? { errorMessage: terminal.errorMessage } : {}),
                };
            }
        }
        groups.push({ type: 'forget-memory', suggestion });
        return true;
    }
    if (part.type === 'data-forget-memory-removed' ||
        part.type === 'data-forget-memory-dismissed' ||
        part.type === 'data-forget-memory-error') {
        // Same-message merge: find the most recent pending forget-memory group
        // within THIS message's parts and fold the terminal state in via the pure
        // helper. Standalone terminal parts on later messages were already absorbed
        // by `forgetMemoryTerminalStates` and produce nothing here.
        const d = (part.data ?? {});
        if (!d.suggestionId)
            return true;
        for (let i = groups.length - 1; i >= 0; i--) {
            const g = groups[i];
            if (g.type === 'forget-memory' && g.suggestion.suggestionId === d.suggestionId) {
                const next = applyForgetMemoryPart(g.suggestion, part);
                if (next)
                    g.suggestion = next;
                break;
            }
        }
        return true;
    }
    return false;
}
function handleApprovalPart(part, groups, opts) {
    if (part.type === 'data-approval-pending') {
        // Phase 5 (D-02 SSE family): a `data-approval-pending` part opens an
        // approval gate. Mirrors the data-memory-suggestion branch — look
        // up the cross-message terminal-state map for this approvalId so
        // the card renders directly in its terminal state if the user has
        // already decided (continuation-turn replay).
        const d = (part.data ?? {});
        if (!d.approvalId || !d.payloadHash || !d.toolName || !d.toolCallId)
            return true;
        const terminal = opts?.approvalTerminalStates?.get(d.approvalId);
        const approval = {
            approvalId: d.approvalId,
            toolName: d.toolName,
            toolCallId: d.toolCallId,
            args: normalizeApprovalArgs(d.toolName, d.args),
            payloadHash: d.payloadHash,
            expiresAt: d.expiresAt ?? '',
            summary: d.summary,
            references: d.references,
            // COP-093 — pass these through only when the SSE part carries them.
            ...(typeof d.trackId === 'string' || d.trackId === null ? { trackId: d.trackId } : {}),
            ...(d.alreadyAlwaysGranted === true ? { alreadyAlwaysGranted: true } : {}),
            state: terminal?.state ?? 'pending',
            // ATRU-01 — execution outcome for an already-approved (e.g. reloaded) card.
            ...(terminal?.state === 'approved' && terminal.execution
                ? { execution: terminal.execution }
                : {}),
            ...(terminal && (terminal.state === 'rejected' || terminal.state === 'edited')
                ? { reason: terminal.reason }
                : {}),
            ...(terminal?.state === 'error'
                ? { errorCode: terminal.errorCode, errorMessage: terminal.errorMessage }
                : {}),
        };
        groups.push({ type: 'approval', approval });
        return true;
    }
    if (part.type === 'data-approval-resolved' ||
        part.type === 'data-approval-error') {
        // Same-message merge — find the most recent pending approval group
        // for this approvalId and upgrade it. Standalone terminal parts on
        // later messages were already absorbed by `approvalTerminalStates`
        // during the cross-message pre-pass and produce nothing here.
        const d = (part.data ?? {});
        if (!d.approvalId)
            return true;
        for (let i = groups.length - 1; i >= 0; i--) {
            const g = groups[i];
            if (g.type === 'approval' && g.approval.approvalId === d.approvalId) {
                if (part.type === 'data-approval-resolved') {
                    const action = d.action;
                    if (action === 'approve') {
                        g.approval = {
                            ...g.approval,
                            state: 'approved',
                            ...(d.execution ? { execution: d.execution } : {}),
                        };
                    }
                    else if (action === 'reject') {
                        g.approval = { ...g.approval, state: 'rejected', reason: d.reason };
                    }
                    else if (action === 'edit') {
                        g.approval = { ...g.approval, state: 'edited', reason: d.reason };
                    }
                }
                else {
                    // data-approval-error
                    if (d.code === 'expired') {
                        g.approval = { ...g.approval, state: 'expired' };
                    }
                    else {
                        g.approval = {
                            ...g.approval,
                            state: 'error',
                            errorCode: d.code,
                            errorMessage: d.message,
                        };
                    }
                }
                break;
            }
        }
        return true;
    }
    return false;
}
function handleDbTablePart(part, groups) {
    if (part.type !== 'data-db-table')
        return false;
    // M24 (COP-114 §7): the server (and the task-07 hydrate path) emits the
    // full `DbTablePartData` payload verbatim. Graceful degradation on the
    // structural essentials only — malformed parts drop silently, matching
    // the sibling normalizers.
    const d = part.data;
    if (!d ||
        typeof d.id !== 'string' ||
        !Array.isArray(d.columns) ||
        !Array.isArray(d.rows))
        return true;
    groups.push({ type: 'db-table', data: d });
    return true;
}
function handleToolPart(part, groups) {
    if (!part.type.startsWith('tool-'))
        return false;
    // The suggest_memory, suggest_skill, and loadSkill tools have dedicated
    // user-facing data-* parts, NOT generic tool-call dropdowns. Suppress
    // the dropdowns to avoid duplicate/noisy details and to preserve the
    // no-content-in-chat boundary for loaded/proposed skill markdown.
    // (COP-037, COP-042, COP-051, COP-067, COP-070)
    if (part.type === 'tool-suggest_memory' ||
        part.type === 'tool-suggest_skill' ||
        part.type === 'tool-loadSkill' ||
        // share_document's user-facing surface is the data-doc-share-suggestion
        // card (IMEM-09, HUB-065, COP-051), not a generic tool-call dropdown.
        part.type === 'tool-share_document' ||
        // forget_memory's user-facing surface is the data-forget-memory
        // confirmation card (IMEM-19, COP-105), not a generic tool-call dropdown.
        part.type === 'tool-forget_memory')
        return true;
    const tool = {
        toolName: part.type.replace('tool-', ''),
        state: part.state,
        input: part.input,
        output: part.output,
    };
    const last = groups[groups.length - 1];
    if (last && last.type === 'tools')
        last.tools.push(tool);
    else
        groups.push({ type: 'tools', tools: [tool] });
    return true;
}
/**
 * Group raw UIMessage parts into renderable units:
 *   - 'text' parts pass through as `{ type: 'text', text }`. When `role==='user'`
 *     and the text is a memory envelope (per `isMemoryEnvelopeText`), it is
 *     dropped — the card's terminal state is the user-visible feedback.
 *   - Consecutive `tool-*` parts merge into one `{ type: 'tools', tools: [...] }`
 *     so multiple tool calls in a single assistant turn render as one block.
 *     `tool-suggest_memory` is suppressed — its user-facing surface is the
 *     `data-memory-suggestion` card (COP-051, MEM-09).
 *   - `data-skill-loaded` opens a lightweight `{ type: 'skill-loaded', skill }`
 *     status group. The raw part is sanitized down to metadata fields only;
 *     malformed parts without `skillId` or `name` are ignored, and legacy
 *     `contentMarkdown` fields are never copied into UI state.
 *   - `data-tf-plan-pending` opens a `{ type: 'plan', plan }` group. The
 *     stream payload is normalized into the shared redacted preview shape only:
 *     required plan/hash/step fields must be present, malformed parts are
 *     ignored, and executable step `args` are never copied.
 *   - `data-memory-suggestion` opens a `{ type: 'memory-suggestion', suggestion }`
 *     entry. When `opts.terminalStates` has an entry for the matching
 *     `suggestionId` (built via `collectMemorySuggestionTerminalStates` over
 *     the entire message list), the entry is upgraded to its terminal state
 *     directly. Otherwise it renders `pending`. Same-message
 *     `data-memory-saved` / `data-memory-dismissed` / `data-memory-suggestion-error`
 *     parts still merge in place; standalone terminal parts on later messages
 *     are absorbed by `terminalStates` and produce no visible group of their own
 *     (the assistant message that carried only the terminal part ends up with
 *     empty groups — the renderer's `groups.length === 0` guard suppresses it).
 */
export function groupParts(parts, opts) {
    const groups = [];
    // Flat ordered dispatch: each handler owns its `part.type` family and returns
    // `true` once it consumes the part. Handler order is the legacy branch order.
    for (const part of parts) {
        if (handleTextPart(part, groups, opts))
            continue;
        if (handleSkillLoadedPart(part, groups))
            continue;
        if (handlePlanPart(part, groups, opts))
            continue;
        if (handleSchedulePreviewPart(part, groups, opts))
            continue;
        if (handleMemoryPart(part, groups, opts))
            continue;
        if (handleDocSharePart(part, groups, opts))
            continue;
        if (handleForgetMemoryPart(part, groups, opts))
            continue;
        if (handleApprovalPart(part, groups, opts))
            continue;
        if (handleDbTablePart(part, groups))
            continue;
        if (handleToolPart(part, groups))
            continue;
    }
    return groups;
}
/**
 * Render a group of consecutive tool calls. Pass a render-prop child to
 * fully customize; falls back to a minimal unstyled default.
 */
export function CopilotToolCall({ tools, children }) {
    const anyRunning = tools.some((t) => t.state === 'input-streaming' || t.state === 'input-available');
    const anyError = tools.some((t) => t.state === 'output-error');
    if (children) {
        return _jsx(_Fragment, { children: children({ tools, anyRunning, anyError }) });
    }
    // Unstyled default — consumers override via .copilot-tool-call classes.
    const label = anyRunning
        ? tools.length === 1
            ? `Running ${tools[0].toolName}…`
            : `Running ${tools.length} tools…`
        : tools.length === 1
            ? `Used ${tools[0].toolName}`
            : `Used ${tools.length} tools`;
    return (_jsxs("details", { className: "copilot-tool-call", "data-running": anyRunning || undefined, "data-error": anyError || undefined, children: [_jsx("summary", { className: "copilot-tool-call-summary", children: label }), _jsx("ul", { className: "copilot-tool-call-list", children: tools.map((tool, i) => (_jsxs("li", { className: "copilot-tool-call-item", children: [_jsx("span", { className: "copilot-tool-call-name", children: tool.toolName }), tool.input && Object.keys(tool.input).length > 0 && (_jsxs("span", { className: "copilot-tool-call-args", children: [' ', Object.entries(tool.input)
                                    .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
                                    .join(', ')] }))] }, i))) })] }));
}
//# sourceMappingURL=tool-call.js.map